import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Menu, X, Phone, ExternalLink } from "lucide-react";
import { useState } from "react";
import LSSDLogo from "./LSSDLogo";
import { useAuth } from "../context/AuthContext";

const NAV_ITEMS = [
  { to: "/", label: "Home", end: true, testId: "nav-link-home" },
  { to: "/storia", label: "Storia", testId: "nav-link-storia" },
  { to: "/codice-penale", label: "Codice Penale", testId: "nav-link-penal" },
  { to: "/roster", label: "Organico", testId: "nav-link-roster" },
  { to: "/arruolamento", label: "Arruolamento", testId: "nav-link-recruitment" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-[13px] font-medium px-4 py-2 whitespace-nowrap rounded-full transition-all ${
      isActive
        ? "bg-[var(--lssd-khaki)] text-[var(--lssd-olive-deep)]"
        : "text-[var(--lssd-cream)]/85 hover:text-[var(--lssd-cream)] hover:bg-white/5"
    }`;

  return (
    <header data-testid="public-navbar" className="sticky top-0 z-40">
      {/* ============ UTILITY STRIP (LASD-style) ============ */}
      <div className="utility-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-9 flex items-center justify-between gap-4">
          <div className="hidden sm:flex items-center gap-5">
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[var(--lssd-khaki)] font-semibold">Emergenza</span>
              <span className="tracking-widest">911</span>
            </span>
            <span className="opacity-40">|</span>
            <a
              href="https://discord.gg/JQE6UTAuP9"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 transition-colors"
              data-testid="utility-discord"
            >
              <ExternalLink className="h-3 w-3" /> Community Discord
            </a>
          </div>
          <div className="flex items-center gap-5 ml-auto text-[10.5px]">
            <span className="hidden md:inline uppercase tracking-[0.2em] text-[var(--lssd-khaki)]/80">
              A Tradition of Service Since 1852
            </span>
          </div>
        </div>
      </div>

      {/* ============ MAIN BAR ============ */}
      <div className="bg-[var(--lssd-olive-deep)] border-b border-[var(--lssd-khaki)]/20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-[76px]">
          <Link to="/" className="flex items-center gap-3.5" data-testid="navbar-home-link">
            <LSSDLogo className="h-11 w-11 shrink-0" />
            <div className="leading-tight">
              <div className="font-display font-black text-[var(--lssd-cream)] text-[19px] tracking-tight">
                Los Santos County
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--lssd-khaki)] font-semibold">
                Sheriff&apos;s Department
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass} data-testid={item.testId}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user && user !== false ? (
              <>
                <NavLink to="/dashboard" className={linkClass} data-testid="nav-link-dashboard">
                  Dashboard
                </NavLink>
                <button
                  onClick={async () => { await logout(); nav("/"); }}
                  data-testid="navbar-logout-btn"
                  className="text-[12px] px-3 py-2 text-[var(--lssd-cream)]/70 hover:text-[var(--lssd-khaki)] inline-flex items-center gap-1.5 font-medium"
                >
                  <LogOut className="h-3.5 w-3.5" /> Esci
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="navbar-login-cta">
                <span className="btn-gold text-[11px] px-4 py-2.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> Portale Operatori
                </span>
              </Link>
            )}
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            className="lg:hidden text-[var(--lssd-cream)] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[var(--lssd-khaki)]/20 px-4 py-5 flex flex-col gap-1 bg-[var(--lssd-olive-deep)]">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-[13px] py-2.5 px-4 rounded-full ${
                    isActive
                      ? "bg-[var(--lssd-khaki)] text-[var(--lssd-olive-deep)] font-semibold"
                      : "text-[var(--lssd-cream)]/85"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {user && user !== false ? (
              <>
                <NavLink to="/dashboard" onClick={() => setOpen(false)} className="text-[13px] py-2.5 px-4 text-[var(--lssd-cream)]/85">
                  Dashboard
                </NavLink>
                <button
                  onClick={async () => { await logout(); setOpen(false); nav("/"); }}
                  className="text-[13px] py-2.5 px-4 text-left text-[var(--lssd-cream)]/85"
                >
                  Esci
                </button>
              </>
            ) : (
              <Link to="/login" className="mt-2" onClick={() => setOpen(false)}>
                <span className="btn-gold w-full justify-center">
                  <ShieldCheck className="h-3.5 w-3.5" /> Portale Operatori
                </span>
              </Link>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
